import React, { useRef, useState } from "react";
import { Mic, Square, Trash2, Loader2, Upload, Volume2 } from "lucide-react";
import { toast } from "sonner";
import { api, BACKEND_URL } from "../lib/api";

/**
 * CardAudio: dual-mode audio input for a card.
 *   - Record from mic (MediaRecorder)
 *   - Upload from disk (any audio file — mp3/wav/ogg/m4a/webm, max 2MB)
 *
 * Props:
 *   cardId       (required) — target card id
 *   existingUrl            — /api/files/{id} to show as current audio
 *   onUploaded(url|null)   — called after upload or clear
 */
export default function CardAudio({ cardId, existingUrl, onUploaded }) {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(existingUrl || null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const fileRef = useRef(null);

  const send = async (blob, filename, contentType) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, filename);
      const { data } = await api.post(`/cards/${cardId}/audio`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const full = `${BACKEND_URL}${data.audio_url}`;
      setPreviewUrl(full);
      onUploaded?.(data.audio_url);
      toast.success("Audio saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const startRec = async () => {
    if (!cardId) return toast.error("Save the card first");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        await send(blob, "clip.webm", "audio/webm");
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch {
      toast.error("Microphone permission denied");
    }
  };

  const stopRec = () => {
    try { mediaRef.current?.stop(); } catch {}
    setRecording(false);
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      toast.error("File too large (max 2MB)");
      e.target.value = "";
      return;
    }
    if (!f.type.startsWith("audio/")) {
      toast.error("Please pick an audio file");
      e.target.value = "";
      return;
    }
    await send(f, f.name, f.type);
    e.target.value = "";
  };

  const clear = () => {
    setPreviewUrl(null);
    onUploaded?.(null);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`card-audio-${cardId || "new"}`}>
      {previewUrl && !uploading && (
        <audio controls src={previewUrl} className="h-9" data-testid={`card-audio-preview-${cardId || "new"}`} />
      )}
      {!recording ? (
        <>
          <button
            type="button"
            onClick={startRec}
            disabled={uploading || !cardId}
            data-testid={`card-audio-record-${cardId || "new"}`}
            className="brutal-btn-white text-xs py-1.5"
            title={cardId ? "Record from mic" : "Save card first"}
          >
            <Mic className="w-3.5 h-3.5" strokeWidth={3} /> Record
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="audio/*,.mp3,.wav,.ogg,.m4a,.webm"
            onChange={onPickFile}
            className="hidden"
            data-testid={`card-audio-file-input-${cardId || "new"}`}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading || !cardId}
            data-testid={`card-audio-upload-${cardId || "new"}`}
            className="brutal-btn-white text-xs py-1.5"
            title={cardId ? "Upload audio file" : "Save card first"}
          >
            <Upload className="w-3.5 h-3.5" strokeWidth={3} /> Upload
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={stopRec}
          data-testid={`card-audio-stop-${cardId || "new"}`}
          className="brutal-btn-pink text-xs py-1.5 animate-pulse"
        >
          <Square className="w-3.5 h-3.5" strokeWidth={3} /> Stop
        </button>
      )}
      {uploading && (
        <span className="text-xs font-bold text-brand-navy/70 inline-flex items-center gap-1">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…
        </span>
      )}
      {previewUrl && !uploading && (
        <button
          type="button"
          onClick={clear}
          data-testid={`card-audio-clear-${cardId || "new"}`}
          className="brutal-btn-white text-xs py-1.5"
          title="Remove audio (removes reference, file remains in storage)"
        >
          <Trash2 className="w-3.5 h-3.5" strokeWidth={3} />
        </button>
      )}
    </div>
  );
}
